import { EntityId, createSelector } from "@reduxjs/toolkit";

import { RootState } from "app/store";

import { CaseViewerState, persistedRecordingAdapter } from "./state";
import { caseViewerSelectors } from "./selectors";

import { getPersistedRecordingWithPlaybackState } from "./helpers";

import { UserPlayMode } from "../types/UserPlayMode";
import { Dossier } from "types/Dossier";
import { PersistedRecording } from "types/PersistedRecording";
import { DossierDevice } from "types/DossierDevice";
import { PersistedRecordingDownloadStatus } from "types/PersistedRecordingDownloadStatus";
import { DossierCapabilityName } from "types/DossierCapabilityName";
import { RecordingPlaybackDirection } from "../types/RecordingPlaybackDirection";
import { userSelectors } from "features/user/store/selectors";
import { luxonUtils } from "utils/luxonUtils";
import { DateTime } from "luxon";

const defaultGridViewMaxNumberOfDevicesPerGridRow = 3; // Default value if no devices are selected

const selectPlaybackSpeedOptions = createSelector(
    [caseViewerSelectors.selectDossier],
    (dossier: Dossier | undefined) =>
        dossier?.dossierCapabilityNames.some(
            (x) =>
                x === DossierCapabilityName.ReversePlaybackUsingReversedVideos,
        )
            ? [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16]
            : [0.5, 1, 2, 8, 16],
);

const selectPlaybackSpeed = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) => state.playbackSpeed,
);

const selectRecordingPlaybackDirection = createSelector(
    [selectPlaybackSpeed],
    (playbackSpeed: number): RecordingPlaybackDirection =>
        playbackSpeed > 0 ? "forward" : "reverse",
);

const selectCurrentPlaybackTimeStamp = createSelector(
    [caseViewerSelectors.selectCaseViewer, caseViewerSelectors.selectDossier],
    (state: CaseViewerState, dossier: Dossier | undefined) => {
        if (state.playbackStartDateTime) {
            return luxonUtils
                .fromIsoWithFallback(
                    state.playbackStartDateTime,
                    DateTime.now(),
                )
                .plus({
                    milliseconds:
                        state.playbackElapsedTimeInMs * state.playbackSpeed,
                })
                .valueOf();
        }

        return luxonUtils
            .fromIsoWithFallback(dossier?.startTime, DateTime.now())
            .valueOf();
    },
);

const {
    selectTotal: selectTotalPersistedRecordings,
    selectAll: selectAllPersistedRecordings,
    selectById: selectPersistedRecordingById,
} = persistedRecordingAdapter.getSelectors(
    (state: RootState) => state.cases.caseViewer.persistedRecordings,
);

const selectAllPersistedRecordingsByDossierDeviceId = createSelector(
    [
        selectAllPersistedRecordings,
        (state: RootState, dossierDeviceId: EntityId) => dossierDeviceId,
    ],
    (persistedRecording: PersistedRecording[], dossierDeviceId: EntityId) => {
        return persistedRecording.filter(
            (x) => x.dossierDeviceId === dossierDeviceId,
        );
    },
);

// https://github.com/reduxjs/reselect#q-can-i-share-a-selector-across-multiple-component-instances
const selectPersistedRecordingsByDossierDeviceIdFactory = () =>
    selectAllPersistedRecordingsByDossierDeviceId;

const selectBufferDateTimeRange = createSelector(
    [caseViewerSelectors.selectCaseViewer, selectRecordingPlaybackDirection],
    (state: CaseViewerState, playbackDirection: RecordingPlaybackDirection) => {
        // from current playback position + X seconds
        const currentPlaybackPosition = luxonUtils
            .fromIsoWithFallback(state.playbackStartDateTime, DateTime.now())
            .plus({
                milliseconds:
                    state.playbackElapsedTimeInMs * state.playbackSpeed,
            });
        const bufferWindowInSecondsMultiplier = Math.min(
            Math.abs(state.playbackSpeed) / 4,
            1,
        ); // 16 speed should multiply seconds by 4 and don't buffer less then the configured window

        if (playbackDirection === "forward") {
            return {
                startDateTime: currentPlaybackPosition.toISO() as string,
                endDateTime: currentPlaybackPosition
                    .plus({
                        seconds:
                            state.bufferWindowInSeconds *
                            bufferWindowInSecondsMultiplier,
                    })
                    .toISO() as string,
            };
        }

        return {
            startDateTime: currentPlaybackPosition
                .plus({
                    seconds:
                        state.bufferWindowInSeconds *
                        bufferWindowInSecondsMultiplier *
                        -1,
                })
                .toISO() as string,
            endDateTime: currentPlaybackPosition.toISO() as string,
        };
    },
);

const selectDeviceRecordingWithPlaybackStateByIdFactory = () =>
    createSelector(
        [
            selectPersistedRecordingById,
            selectCurrentPlaybackTimeStamp,
            selectBufferDateTimeRange,
        ],
        (
            persistedRecording: PersistedRecording | undefined,
            currentPlaybackTimeStamp: number,
            bufferWindow: { startDateTime: string; endDateTime: string },
        ) => {
            if (!persistedRecording) return undefined;

            const bufferStartDateTimeTicks = DateTime.fromISO(
                bufferWindow.startDateTime,
            ).valueOf();
            const bufferEndDateTimeTicks = DateTime.fromISO(
                bufferWindow.endDateTime,
            ).valueOf();

            return getPersistedRecordingWithPlaybackState(
                bufferStartDateTimeTicks,
                bufferEndDateTimeTicks,
                currentPlaybackTimeStamp,
                persistedRecording,
            );
        },
    );

const selectUserRequestedPlayMode = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) => state.userRequestedPlayMode,
);

const selectPersistedRecordingsByDossierDeviceIdInPlaybackWindowFactory =
    () => {
        return createSelector(
            [
                selectAllPersistedRecordingsByDossierDeviceId,
                selectCurrentPlaybackTimeStamp,
            ],
            (
                persistedRecordings: PersistedRecording[],
                currentPlaybackTimeStamp: number,
            ) => {
                return persistedRecordings.filter((recording) => {
                    const startTimeTicks = DateTime.fromISO(
                        recording.persistedStartTime,
                    ).valueOf();
                    const stopTimeTicks = DateTime.fromISO(
                        recording.persistedStopTime,
                    ).valueOf();

                    return (
                        startTimeTicks <= currentPlaybackTimeStamp &&
                        stopTimeTicks >= currentPlaybackTimeStamp
                    );
                });
            },
        );
    };

const selectAllPersistedRecordingsWithDeviceName = createSelector(
    [
        selectAllPersistedRecordings,
        caseViewerSelectors.selectSelectedDossierDevices,
    ],
    (
        persistedRecordings: PersistedRecording[],
        dossierDevices: DossierDevice[],
    ) => {
        return persistedRecordings.map((recording) => {
            return {
                ...recording,
                deviceName: dossierDevices.find(
                    (d) => d.id === recording.dossierDeviceId,
                )?.name,
            };
        });
    },
);

const selectPersistedRecordingsReadyForPlayback = createSelector(
    [
        selectRecordingPlaybackDirection,
        (state: RootState) =>
            state.cases.caseViewer.persistedRecordingsReadyForPlayback,
        (state: RootState) =>
            state.cases.caseViewer.reversedPersistedRecordingsReadyForPlayback,
    ],
    (
        playbackDirection: RecordingPlaybackDirection,
        recordingsReadyForPlayback: EntityId[],
        reversedRecordingsReadyForPlayback: EntityId[],
    ) =>
        playbackDirection === "forward"
            ? recordingsReadyForPlayback
            : reversedRecordingsReadyForPlayback,
);
const selectPersistedRecordingsFailedToLoad = createSelector(
    [
        selectRecordingPlaybackDirection,
        (state: RootState) =>
            state.cases.caseViewer.persistedRecordingsFailedToLoad,
        (state: RootState) =>
            state.cases.caseViewer.reversedPersistedRecordingsFailedToLoad,
    ],
    (
        playbackDirection: RecordingPlaybackDirection,
        recordingsFailedToLoad: EntityId[],
        reversedRecordingsFailedToLoad: EntityId[],
    ) =>
        playbackDirection === "forward"
            ? recordingsFailedToLoad
            : reversedRecordingsFailedToLoad,
);
const selectPersistedRecordingsWaiting = createSelector(
    [
        selectRecordingPlaybackDirection,
        (state: RootState) => state.cases.caseViewer.persistedRecordingsWaiting,
        (state: RootState) =>
            state.cases.caseViewer.reversedPersistedRecordingsWaiting,
    ],
    (
        playbackDirection: RecordingPlaybackDirection,
        recordingsWaiting: EntityId[],
        reversedRecordingsWaiting: EntityId[],
    ) =>
        playbackDirection === "forward"
            ? recordingsWaiting
            : reversedRecordingsWaiting,
);

const selectReadyForPlaybackStatistics = createSelector(
    [
        selectAllPersistedRecordings,
        selectCurrentPlaybackTimeStamp,
        selectPersistedRecordingsReadyForPlayback,
        selectPersistedRecordingsFailedToLoad,
        selectPersistedRecordingsWaiting,
    ],
    (
        persistedRecordings: PersistedRecording[],
        currentPlaybackTimeStamp: number,
        recordingsReadyForPlayback: EntityId[],
        recordingsFailedToLoad: EntityId[],
        recordingsWaiting: EntityId[],
    ) => {
        const recordingsWhichShouldBePlaying = persistedRecordings
            .filter(
                (persistedRecording) =>
                    persistedRecording.status ===
                    PersistedRecordingDownloadStatus.Completed,
            )
            .filter((persistedRecording) => {
                const startTimeTicks = DateTime.fromISO(
                    persistedRecording.persistedStartTime,
                ).valueOf();
                const stopTimeTicks = DateTime.fromISO(
                    persistedRecording.persistedStopTime,
                ).valueOf();

                return (
                    startTimeTicks <= currentPlaybackTimeStamp &&
                    stopTimeTicks >= currentPlaybackTimeStamp
                );
            });

        const readyForPlayback = recordingsWhichShouldBePlaying.filter(
            (recording) => recordingsReadyForPlayback.includes(recording.id),
        ).length;

        const failedToLoad = recordingsWhichShouldBePlaying.filter(
            (recording) => recordingsFailedToLoad.includes(recording.id),
        ).length;

        const waiting = recordingsWhichShouldBePlaying.filter((recording) =>
            recordingsWaiting.includes(recording.id),
        ).length;

        return {
            totalPersistedRecordings: recordingsWhichShouldBePlaying.length,
            totalReadyForPlayback: readyForPlayback,
            totalFailedToLoad: failedToLoad,
            totalWaiting: waiting,
            percentageReadyForPlayback:
                ((readyForPlayback + failedToLoad) /
                    recordingsWhichShouldBePlaying.length) *
                100,
            ready:
                recordingsWhichShouldBePlaying.length -
                    readyForPlayback -
                    failedToLoad <=
                0,
        };
    },
);

const selectWhichDirectionToShowByIdFactory = (
    persistedRecordingId: EntityId,
) =>
    createSelector(
        [
            selectRecordingPlaybackDirection,
            (state: RootState) =>
                state.cases.caseViewer.persistedRecordingsReadyForPlayback,
            (state: RootState) =>
                state.cases.caseViewer
                    .reversedPersistedRecordingsReadyForPlayback,
        ],
        (
            playbackDirection: RecordingPlaybackDirection,
            recordingsReadyForPlayback: EntityId[],
            reversedRecordingsReadyForPlayback: EntityId[],
        ): RecordingPlaybackDirection => {
            const forwardReady = recordingsReadyForPlayback.some(
                (x) => x === persistedRecordingId,
            );
            const reversedReady = reversedRecordingsReadyForPlayback.some(
                (x) => x === persistedRecordingId,
            );

            if (playbackDirection === "forward") {
                return !forwardReady && reversedReady ? "reverse" : "forward";
            }

            return !reversedReady && forwardReady ? "forward" : "reverse";
        },
    );

const selectPlaybackPlaying = createSelector(
    [selectUserRequestedPlayMode, selectReadyForPlaybackStatistics],
    (
        userRequestedPlayMode: UserPlayMode,
        readyForPlaybackStatistics: {
            totalPersistedRecordings: number;
            totalReadyForPlayback: number;
            percentageReadyForPlayback: number;
            ready: boolean;
        },
    ) => {
        // check if user wants to play
        if (
            userRequestedPlayMode !== "play" &&
            userRequestedPlayMode !== "resume"
        )
            return false;

        return readyForPlaybackStatistics.ready;
    },
);

const selectPlaybackStartDateTimeChangedTimestamp = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) => state.playbackStartDateTimeChangedTimestamp,
);

const selectIsWaitingForPersistedRecordingsToBecomeReady = createSelector(
    [selectUserRequestedPlayMode, selectPlaybackPlaying],
    (userRequestedPlayMode: UserPlayMode, isPlaying: boolean) =>
        (userRequestedPlayMode === "play" ||
            userRequestedPlayMode === "resume") &&
        !isPlaying,
);

const selectPersistedRecordingFailedToLoadByIdFactory = () =>
    createSelector(
        [
            selectPersistedRecordingById,
            (state: RootState) =>
                state.cases.caseViewer.persistedRecordingsFailedToLoad,
        ],
        (
            persistedRecording: PersistedRecording | undefined,
            recordingsFailedToLoad: EntityId[],
        ) =>
            persistedRecording &&
            recordingsFailedToLoad.some((x) => x === persistedRecording.id),
    );

const selectPlaybackStartDateTime = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) =>
        state.playbackStartDateTime || state.dossier?.startTime,
);

const selectBufferWindow = createSelector(
    [
        (state: RootState) => state.cases.caseViewer.playbackStartDateTime,
        (state: RootState) => state.cases.caseViewer.playbackElapsedTimeInMs,
        (state: RootState) => state.cases.caseViewer.playbackSpeed,
    ],
    (
        playbackStartDateTime: string | undefined,
        playbackElapsedTimeInMs: number,
        playbackSpeed: number,
    ):
        | {
              startDateTime: string | undefined;
              endDateTime: string | undefined;
          }
        | undefined => {
        const maxEndDateTime = playbackStartDateTime
            ? luxonUtils
                  .fromIsoWithFallback(
                      playbackStartDateTime,
                      DateTime.fromMillis(0),
                  )
                  .plus({
                      milliseconds: playbackElapsedTimeInMs * playbackSpeed,
                  })
            : undefined;

        return {
            startDateTime: playbackStartDateTime,
            endDateTime: luxonUtils.toISO(maxEndDateTime),
        };
    },
);

const selectVisibleDossierDeviceIds = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) => state.visibleDossierDeviceIds,
);
const selectFullscreenDossierDeviceId = createSelector(
    [caseViewerSelectors.selectCaseViewer],
    (state: CaseViewerState) => state.fullscreenDossierDeviceId,
);
const selectAllPersistedRecordingIdsForInvisibleDossierDevices = createSelector(
    [selectVisibleDossierDeviceIds, selectAllPersistedRecordings],
    (
        visibleDossierDeviceIds: EntityId[],
        persistedRecordings: PersistedRecording[],
    ) =>
        persistedRecordings
            .filter((x) => !visibleDossierDeviceIds.includes(x.dossierDeviceId))
            .map((x) => x.id),
);

const selectCurrentTimeByPersistedRecordingIdOnEveryTickFactory = (
    direction: RecordingPlaybackDirection,
) =>
    createSelector(
        [
            caseViewerSelectors.selectCaseViewer,
            selectPersistedRecordingById,
            selectCurrentPlaybackTimeStamp,
        ],
        (
            state: CaseViewerState,
            persistedRecording: PersistedRecording | undefined,
            currentPlaybackTimeStamp: number,
        ) => {
            let currentTime = 0;

            if (persistedRecording) {
                const playbackDateTime = DateTime.fromMillis(
                    currentPlaybackTimeStamp,
                );
                const recordingStartTime = DateTime.fromISO(
                    persistedRecording.persistedStartTime,
                );
                const recordingStopTime = DateTime.fromISO(
                    persistedRecording.persistedStopTime,
                );

                if (
                    playbackDateTime.toMillis() >=
                        recordingStartTime.toMillis() &&
                    playbackDateTime.toMillis() <= recordingStopTime.toMillis()
                ) {
                    if (direction === "forward") {
                        currentTime =
                            playbackDateTime.diff(
                                recordingStartTime,
                                "milliseconds",
                            ).milliseconds / 1000;
                    } else {
                        currentTime = Math.abs(
                            playbackDateTime.diff(
                                recordingStopTime,
                                "milliseconds",
                            ).milliseconds / 1000,
                        );
                    }
                }
            }

            return {
                currentTime,
                playbackStartDateTimeChangedTimestamp:
                    state.playbackStartDateTimeChangedTimestamp,
            };
        },
    );

const selectCurrentTimeByPersistedRecordingIdFactory = (
    direction: RecordingPlaybackDirection,
) =>
    createSelector(
        [caseViewerSelectors.selectCaseViewer, selectPersistedRecordingById],
        (
            state: CaseViewerState,
            persistedRecording: PersistedRecording | undefined,
        ) => {
            let currentTime = 0;

            if (persistedRecording) {
                const playbackStartDateTimeLuxon =
                    luxonUtils.fromIsoWithFallback(
                        state.playbackStartDateTime,
                        DateTime.now(),
                    );
                const recordingStartTime = DateTime.fromISO(
                    persistedRecording.persistedStartTime,
                );
                const recordingStopTime = DateTime.fromISO(
                    persistedRecording.persistedStopTime,
                );

                if (direction === "forward") {
                    // determine forward currentTime
                    if (
                        playbackStartDateTimeLuxon.toMillis() >=
                            recordingStartTime.toMillis() &&
                        playbackStartDateTimeLuxon.toMillis() <=
                            recordingStopTime.toMillis()
                    ) {
                        currentTime =
                            playbackStartDateTimeLuxon.diff(
                                recordingStartTime,
                                "milliseconds",
                            ).milliseconds / 1000;
                    }
                } else {
                    // determine reversed currentTime
                    if (
                        playbackStartDateTimeLuxon.toMillis() >=
                            recordingStartTime.toMillis() &&
                        playbackStartDateTimeLuxon.toMillis() <=
                            recordingStopTime.toMillis()
                    ) {
                        currentTime = Math.abs(
                            playbackStartDateTimeLuxon.diff(
                                recordingStopTime,
                                "milliseconds",
                            ).milliseconds / 1000,
                        );
                    }
                }
            }

            return {
                currentTime,
                playbackStartDateTimeChangedTimestamp:
                    state.playbackStartDateTimeChangedTimestamp,
            };
        },
    );

const selectNumberOfDevicesPerRow = createSelector(
    [
        selectVisibleDossierDeviceIds,
        userSelectors.selectGridViewEnableAutoSizing,
        userSelectors.selectGridViewMaxNumberOfDevicesPerGridRow,
    ],
    (
        visibleDossierDeviceIds,
        gridViewEnableAutoSizing,
        gridViewMaxNumberOfDevicesPerGridRow,
    ) => {
        if (!visibleDossierDeviceIds || !gridViewEnableAutoSizing) {
            return defaultGridViewMaxNumberOfDevicesPerGridRow;
        }
        return Math.min(
            gridViewMaxNumberOfDevicesPerGridRow,
            visibleDossierDeviceIds.length || 1,
        );
    },
);

export const playbackSelectors = {
    // data
    selectAllPersistedRecordings,
    selectTotalPersistedRecordings,
    selectPersistedRecordingById,
    selectPersistedRecordingsByDossierDeviceIdFactory,
    selectCurrentTimeByPersistedRecordingIdOnEveryTickFactory,
    selectAllPersistedRecordingsWithDeviceName,
    selectAllPersistedRecordingIdsForInvisibleDossierDevices,

    // timeline
    selectCurrentPlaybackTimeStamp,
    selectPlaybackStartDateTime,
    selectUserRequestedPlayMode,
    selectPlaybackSpeedOptions,
    selectPlaybackSpeed,
    selectRecordingPlaybackDirection,
    selectPlaybackPlaying,
    selectPlaybackStartDateTimeChangedTimestamp,

    // buffering and loading
    selectBufferDateTimeRange,
    selectPersistedRecordingsByDossierDeviceIdInPlaybackWindowFactory,
    selectReadyForPlaybackStatistics,
    selectIsWaitingForPersistedRecordingsToBecomeReady,
    selectPersistedRecordingFailedToLoadByIdFactory,
    selectDeviceRecordingWithPlaybackStateByIdFactory,
    selectBufferWindow,
    selectCurrentTimeByPersistedRecordingIdFactory,
    selectWhichDirectionToShowByIdFactory,

    // user session
    selectVisibleDossierDeviceIds,
    selectFullscreenDossierDeviceId,
    selectNumberOfDevicesPerRow,
};
